import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Menu } from 'antd-v5';
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED } from 'components/Page/PageSideBar/PageSideBar';
import { R1SidebarMenuWrapper } from 'components/Page/PageSideBar/R1SidebarMenu/R1SidebarMenu.styled';
import { R1MenuNavigationMap, useR2SidebarMenuItems } from 'components/Page/PageSideBar/R1SidebarMenu/utils';
import ProfileInfo from 'components/ProfileInfo';
import { Flex } from 'UI';

interface R1SidebarMenuProps {
  collapsed: boolean;
}

const parseTabParam = (search: string): string | null => new URLSearchParams(search).get('tab');

const isLinkActive = (link: string | undefined, activePathname: string, activeTab: string | null): boolean => {
  if (!link) {
    return false;
  }
  const [linkPath, linkQuery] = link.split('?');
  const pathMatches = linkPath === '/' ? activePathname === linkPath : activePathname.startsWith(linkPath);
  if (!pathMatches) {
    return false;
  }
  const linkTab = linkQuery ? new URLSearchParams(linkQuery).get('tab') : null;
  return linkTab ? linkTab === activeTab : !activeTab;
};

const collectAncestorKeys = (key: string, navigationMap: R1MenuNavigationMap, acc: Set<string>): void => {
  const parentKey = navigationMap.get(key)?.parentKey;
  if (parentKey && !acc.has(parentKey)) {
    acc.add(parentKey);
    collectAncestorKeys(parentKey, navigationMap, acc);
  }
};

const getSelectedKeys = (navigationMap: R1MenuNavigationMap, pathname: string, search: string): string[] => {
  const activeTab = parseTabParam(search);
  const activeKeys = new Set<string>();
  navigationMap.forEach((nav, key) => {
    if (!nav.isExternal && isLinkActive(nav.link, pathname, activeTab)) {
      activeKeys.add(key);
    }
  });
  [...activeKeys].forEach((key) => collectAncestorKeys(key, navigationMap, activeKeys));
  return [...activeKeys];
};

const R1SidebarMenu = ({ collapsed }: R1SidebarMenuProps) => {
  const { items, navigationMap } = useR2SidebarMenuItems(collapsed);
  const history = useHistory();
  const { pathname, search } = useLocation();

  const onClick = useCallback(
    ({ key }: { key: string }) => {
      const nav = navigationMap.get(key);
      if (!nav?.link) {
        return;
      }
      if (nav.isExternal) {
        window.open(nav.link, '_blank', 'noopener,noreferrer');
        return;
      }
      history.push(nav.link);
    },
    [navigationMap, history],
  );

  return (
    <Flex direction={'column'}>
      <ProfileInfo mini={collapsed} />
      <R1SidebarMenuWrapper $collapsed={collapsed}>
        <Menu
          onClick={onClick}
          selectedKeys={getSelectedKeys(navigationMap, pathname, search)}
          style={{ width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH }}
          mode={'vertical'}
          items={items}
          expandIcon={collapsed ? null : undefined}
        />
      </R1SidebarMenuWrapper>
    </Flex>
  );
};

export default R1SidebarMenu;
